import { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  addDoc,
  collection,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { uploadImageToGitHub } from "../utils/githubUpload";

export default function CreatePost({ admin }) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [category, setCategory] = useState(null);

  const [categories, setCategories] = useState([]);

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [loading, setLoading] = useState(false);

  /* ================= FETCH CATEGORIES ================= */
  useEffect(() => {
    const q = query(collection(db, "categories"), orderBy("createdAt", "asc"));
    return onSnapshot(q, (snap) =>
      setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
  }, []);

  /* ================= CLEANUP PREVIEW ================= */
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  /* ================= SUBMIT HANDLER ================= */
  const submitPost = async (status) => {
    if (!title.trim() || !desc.trim() || !category) {
      alert("Title, description and category are required");
      return;
    }

    setLoading(true);

    try {
      let imageUrl = "";

      if (file) {
        imageUrl = await uploadImageToGitHub(file);
      }

      await addDoc(collection(db, "posts"), {
  title: title.trim(),
  description: desc.trim(),
  category: category.slug,
  status,
  imageUrl,

  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),

  createdBy: {
    uid: admin.uid,
    email: admin.email,
  },

  updatedBy: {
    uid: admin.uid,
    email: admin.email,
  },
});


      // RESET FORM
      setTitle("");
      setDesc("");
      setCategory(null);
      setFile(null);
      setPreviewUrl(null);
    } catch (err) {
      console.error(err);
      alert("Failed to save post");
    } finally {
      setLoading(false);
    }
  };

  return (
<div className="
  max-w-4xl mx-auto
  bg-neutral-900
  rounded-2xl
  p-8
  shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_20px_40px_rgba(0,0,0,0.6)]
">

    {/* HEADER */}
    <div className="mb-8">
      <h2 className="text-2xl font-semibold">Create Post</h2>
      <p className="text-sm text-neutral-400 mt-1">
        Publish or save articles for Echoes of Engine
      </p>
    </div>

    {/* TITLE + CATEGORY */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div className="md:col-span-2">
        <label className="text-xs text-neutral-400 mb-1 block">
          Post title
        </label>
        <input
          className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-red-500"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div>
        <label className="text-xs text-neutral-400 mb-1 block">
          Category
        </label>
        <select
          className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-red-500"
          value={category?.id || ""}
          onChange={(e) => {
            const selected = categories.find(c => c.id === e.target.value);
            setCategory(selected || null);
          }}
        >
          <option value="">Select</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
    </div>

    {/* DESCRIPTION */}
    <div className="mb-6">
      <label className="text-xs text-neutral-400 mb-1 block">
        Description
      </label>
      <textarea
        rows={6}
        className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-4 py-3 text-sm resize-none focus:outline-none focus:border-red-500"
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
      />
    </div>

    {/* IMAGE UPLOAD */}
    <div className="mb-8">
      <label className="text-xs text-neutral-400 mb-2 block">
        Featured image
      </label>

      <label className="
        block cursor-pointer
        border border-dashed border-neutral-700
        rounded-xl p-6 text-center
        hover:border-red-500 transition
      ">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Preview"
            className="mx-auto max-h-56 rounded-lg object-cover"
          />
        ) : (
          <div className="text-sm text-neutral-400">
            Click to upload image <br />
            <span className="text-xs opacity-60">PNG, JPG, WEBP</span>
          </div>
        )}

        <input
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const selected = e.target.files[0];
            if (!selected) return;
            setFile(selected);
            setPreviewUrl(URL.createObjectURL(selected));
          }}
        />
      </label>
    </div>

    {/* ACTIONS */}
    <div className="flex items-center justify-end gap-3 pt-6 border-t border-neutral-800">
      <button
        onClick={() => submitPost("draft")}
        disabled={loading}
        className="
          px-5 py-2.5 rounded-lg
          border border-neutral-700
          text-sm text-neutral-300
          hover:bg-neutral-800
          transition disabled:opacity-60
        "
      >
        Save Draft
      </button>

      <button
        onClick={() => submitPost("published")}
        disabled={loading}
        className="
          px-6 py-2.5 rounded-lg
          bg-red-600 text-black
          font-semibold text-sm
          hover:bg-red-500
          transition disabled:opacity-60
        "
      >
        {loading ? "Publishing..." : "Publish"}
      </button>
    </div>
  </div>
);

}
